import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Cookie, X, Settings, Check } from 'lucide-react';

const CookieConsent = () => {
  const { language } = useLanguage();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always required
    analytics: false,
    marketing: false
  });

  const translations = {
    it: {
      title: 'Utilizziamo i cookie',
      description: 'Questo sito utilizza cookie per migliorare la tua esperienza di navigazione. I cookie essenziali sono necessari per il funzionamento del sito, mentre altri ci aiutano a capire come viene utilizzato.',
      acceptAll: 'Accetta tutti',
      rejectNonEssential: 'Rifiuta non essenziali',
      managePreferences: 'Gestisci preferenze',
      savePreferences: 'Salva preferenze',
      privacyPolicy: 'Privacy Policy',
      cookiePolicy: 'Cookie Policy',
      settingsTitle: 'Gestione Cookie',
      essential: 'Cookie Essenziali',
      essentialDesc: 'Necessari per il funzionamento del sito. Non possono essere disattivati.',
      analytics: 'Cookie Analitici',
      analyticsDesc: 'Ci aiutano a capire come i visitatori interagiscono con il sito.',
      marketing: 'Cookie di Marketing',
      marketingDesc: 'Utilizzati per fornire annunci più rilevanti.',
      required: 'Richiesto',
      close: 'Chiudi'
    },
    en: {
      title: 'We use cookies',
      description: 'This site uses cookies to improve your browsing experience. Essential cookies are necessary for the site to function, while others help us understand how it is used.',
      acceptAll: 'Accept all',
      rejectNonEssential: 'Reject non-essential',
      managePreferences: 'Manage preferences',
      savePreferences: 'Save preferences',
      privacyPolicy: 'Privacy Policy',
      cookiePolicy: 'Cookie Policy',
      settingsTitle: 'Cookie Settings',
      essential: 'Essential Cookies',
      essentialDesc: 'Necessary for the site to function. Cannot be disabled.',
      analytics: 'Analytics Cookies',
      analyticsDesc: 'Help us understand how visitors interact with the site.',
      marketing: 'Marketing Cookies',
      marketingDesc: 'Used to provide more relevant ads.',
      required: 'Required',
      close: 'Close'
    },
    es: {
      title: 'Utilizamos cookies',
      description: 'Este sitio utiliza cookies para mejorar tu experiencia de navegación. Las cookies esenciales son necesarias para el funcionamiento del sitio, mientras que otras nos ayudan a entender cómo se utiliza.',
      acceptAll: 'Aceptar todas',
      rejectNonEssential: 'Rechazar no esenciales',
      managePreferences: 'Gestionar preferencias',
      savePreferences: 'Guardar preferencias',
      privacyPolicy: 'Política de Privacidad',
      cookiePolicy: 'Política de Cookies',
      settingsTitle: 'Configuración de Cookies',
      essential: 'Cookies Esenciales',
      essentialDesc: 'Necesarias para el funcionamiento del sitio. No se pueden desactivar.',
      analytics: 'Cookies Analíticas',
      analyticsDesc: 'Nos ayudan a entender cómo los visitantes interactúan con el sitio.',
      marketing: 'Cookies de Marketing',
      marketingDesc: 'Utilizadas para proporcionar anuncios más relevantes.',
      required: 'Requerida',
      close: 'Cerrar'
    },
    pt: {
      title: 'Utilizamos cookies',
      description: 'Este site utiliza cookies para melhorar sua experiência de navegação. Os cookies essenciais são necessários para o funcionamento do site, enquanto outros nos ajudam a entender como ele é utilizado.',
      acceptAll: 'Aceitar todos',
      rejectNonEssential: 'Rejeitar não essenciais',
      managePreferences: 'Gerenciar preferências',
      savePreferences: 'Salvar preferências',
      privacyPolicy: 'Política de Privacidade',
      cookiePolicy: 'Política de Cookies',
      settingsTitle: 'Configurações de Cookies',
      essential: 'Cookies Essenciais',
      essentialDesc: 'Necessários para o funcionamento do site. Não podem ser desativados.',
      analytics: 'Cookies Analíticos',
      analyticsDesc: 'Nos ajudam a entender como os visitantes interagem com o site.',
      marketing: 'Cookies de Marketing',
      marketingDesc: 'Utilizados para fornecer anúncios mais relevantes.',
      required: 'Obrigatório',
      close: 'Fechar'
    }
  };

  const t = translations[language] || translations.it;

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = { essential: true, analytics: true, marketing: true };
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setPreferences(allAccepted);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleRejectNonEssential = () => {
    const essentialOnly = { essential: true, analytics: false, marketing: false };
    localStorage.setItem('cookieConsent', JSON.stringify(essentialOnly));
    setPreferences(essentialOnly);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    setShowBanner(false);
    setShowSettings(false);
  };

  const togglePreference = (key) => {
    if (key === 'essential') return; // Cannot disable essential
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!showBanner) return null;

  // Hide cookie banner on admin/CEO routes — they are not end-user pages
  if (typeof window !== 'undefined') {
    const path = window.location.pathname.toLowerCase();
    if (path.startsWith('/ceo') || path.startsWith('/admin')) return null;
  }

  return (
    <>
      {/* Visual dim — pointer-events-none so users can still interact with the page underneath.
          The cookie banner remains dismissable via its own buttons. */}
      <div
        className="fixed inset-0 bg-black/20 z-[9998] pointer-events-none"
        aria-hidden="true"
      />

      {/* Main Banner */}
      {!showSettings && (
        <div 
          className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6 animate-slide-up"
          data-testid="cookie-banner"
        >
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden">
            <div className="p-5 md:p-6">
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex items-center justify-center w-12 h-12 bg-amber-50 rounded-full flex-shrink-0">
                  <Cookie className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2" style={{fontFamily: "'Cormorant Garamond', serif"}}>
                    {t.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    {t.description}
                  </p>
                  <div className="flex flex-wrap gap-2 text-sm mb-4">
                    <Link to="/privacy" className="text-blue-600 hover:text-blue-800 underline">
                      {t.privacyPolicy}
                    </Link>
                    <span className="text-gray-400">•</span>
                    <Link to="/cookies" className="text-blue-600 hover:text-blue-800 underline">
                      {t.cookiePolicy}
                    </Link>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 mt-2">
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 sm:flex-none px-5 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                  data-testid="cookie-accept-all"
                >
                  <Check className="w-4 h-4" />
                  {t.acceptAll}
                </button>
                <button
                  onClick={handleRejectNonEssential}
                  className="flex-1 sm:flex-none px-5 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                  data-testid="cookie-reject"
                >
                  {t.rejectNonEssential}
                </button>
                <button
                  onClick={() => setShowSettings(true)}
                  className="flex-1 sm:flex-none px-5 py-2.5 text-gray-600 font-medium rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
                  data-testid="cookie-settings"
                >
                  <Settings className="w-4 h-4" />
                  {t.managePreferences}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Panel */}
      {showSettings && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          data-testid="cookie-settings-panel"
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900" style={{fontFamily: "'Cormorant Garamond', serif"}}>
                {t.settingsTitle}
              </h3>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label={t.close}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-5 space-y-4 overflow-y-auto max-h-[50vh]">
              {/* Essential Cookies */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{t.essential}</span>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                    {t.required}
                  </span>
                </div>
                <p className="text-sm text-gray-600">{t.essentialDesc}</p>
              </div>

              {/* Analytics Cookies */}
              <div className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{t.analytics}</span>
                  <button
                    onClick={() => togglePreference('analytics')}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      preferences.analytics ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                    aria-label={`Toggle ${t.analytics}`}
                  >
                    <span
                      className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                        preferences.analytics ? 'left-7' : 'left-1'
                      }`}
                    />
                  </button>
                </div>
                <p className="text-sm text-gray-600">{t.analyticsDesc}</p>
              </div>

              {/* Marketing Cookies */}
              <div className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{t.marketing}</span>
                  <button
                    onClick={() => togglePreference('marketing')}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      preferences.marketing ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                    aria-label={`Toggle ${t.marketing}`}
                  >
                    <span
                      className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                        preferences.marketing ? 'left-7' : 'left-1'
                      }`}
                    />
                  </button>
                </div>
                <p className="text-sm text-gray-600">{t.marketingDesc}</p>
              </div>
            </div>

            <div className="p-5 border-t border-gray-100 flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleSavePreferences}
                className="flex-1 px-5 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                data-testid="cookie-save-preferences"
              >
                {t.savePreferences}
              </button>
              <button
                onClick={handleAcceptAll}
                className="flex-1 px-5 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t.acceptAll}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default CookieConsent;
